import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Estacao } from '@home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import { PostosRelatorios } from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { Select } from '@layout/interfaces/select';
import {
	baixarEAbrirPDF,
	filterSelectOptions,
	getBase64ImageFromURL,
	numberToBrNumber,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { DateTimeUtils } from '@utils/datetime-util';
import * as pdfseira from '@utils/pdf-seira';
import { startOfMonth, subYears } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
	Agrupamento,
	IapmDTO,
} from '@home/submodulos/dados-meteorologicos/interfaces';
import { LegendaCor } from '@utils/interpolacao/interpolacao';

export const PERIODO_MINIMO_SPI_EM_ANOS = 10;
export const PERIODO_MAXIMO_SPI_EM_ANOS = 30;

@Component({
	selector: 'seira-pluviometro-grafico-spi',
	templateUrl: './pluviometro-grafico-spi.component.html',
	styleUrls: ['./pluviometro-grafico-spi.component.scss'],
})
export class PluviometroGraficoSpiComponent
	implements InstanciaRelatorio, OnInit, OnDestroy
{
	postos: PostosRelatorios[] = [];
	estacoes: Estacao[] = [];
	microrregioes: Select[] = [];
	agrupamento: Select<string>[];
	private subscription = new Subscription();
	private inputs = inject(INPUTS_RELATORIOS);

	carregandoRelatorio = true;
	graficoSPIUrl?: string;
	graficoSPIBase64?: string;
	grafico = 'SPI';
	valores: LegendaCor[] = [];
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: async () => {
				await this.exportarPDF(this.graficoSPIUrl);
			},
		},
	];
	descricaoRelatorio =
		'Define-se como índice de precipitação padronizado (SPI), a anomalia, em número de desvios padrão, que a pluviometria observada se afasta da climatologia, auxiliando na classificação de períodos secos ou úmidos em um determinado local ou região.';

	referenciaEstudo =
		'Os métodos utilizados para a análise da precipitação e cálculo do Índice de Precipitação Padronizado (SPI) foram baseados nas diretrizes apresentadas no guia climático do National Center for Atmospheric Research (NCAR), intitulado: "Standardized Precipitation Index (SPI)". Este guia fornece os fundamentos teóricos e práticos para a aplicação do SPI, amplamente utilizado para o monitoramento de secas e análise da variabilidade climática. O estudo está disponível no Climate Data Guide.';

	periodoTitulo = '';

	constructor(
		private readonly relatoriosService: RelatoriosService,
		private readonly toastr: ToastrService
	) {
		this.setValidators();
		this.agrupamento = filterSelectOptions(
			Agrupamento,
			Agrupamento.MUNICIPIO_POSTO,
			Agrupamento.REGIAO_PLUVIOMETRICA
		);
	}

	ngOnInit() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento[0].value);

		const inicioDefault = startOfMonth(
			subYears(new Date(), PERIODO_MINIMO_SPI_EM_ANOS)
		);
		const fimDefault = new Date();

		this.inputs.form.patchValue({
			dataInicio: inicioDefault,
			dataFim: fimDefault,
		});
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
	}

	gerarRelatorio(): void {
		const idRegiaoPluviometrica =
			this.getFormItemValue(FormularioRelatorio.AGRUPAMENTO) ===
			'REGIAO_PLUVIOMETRICA'
				? this.getFormItemValue(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
				: null;
		const dataInicio = this.getFormItemValue('dataInicio');
		const dataFim = this.getFormItemValue('dataFim');

		this.carregandoRelatorio = true;
		this.inputs.setLoading(true);
		this.periodoTitulo = this.getPeriodo();
		this.relatoriosService
			.buscarGraficoSPI(dataInicio, dataFim, idRegiaoPluviometrica)
			.subscribe({
				next: async grafico => {
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						!!grafico && grafico.size > 0
					);

					if (this.graficoSPIUrl) {
						URL.revokeObjectURL(this.graficoSPIUrl);
					}

					this.graficoSPIUrl = URL.createObjectURL(grafico);
					this.graficoSPIBase64 = await this.converterBlobParaDataUrl(grafico);
					this.valoresLegenda();
				},
				error: () => {
					this.carregandoRelatorio = false;
					this.inputs.setLoading(false);
					this.toastr.error('Ocorreu um erro ao gerar o relatório');
				},
				complete: () => {
					this.carregandoRelatorio = false;
					this.inputs.setLoading(false);
				},
			});
	}
	valoresLegenda() {
		this.valores = [
			{
				infoAdicional: 'Excepcionalmente úmido',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#00008b',
			},
			{
				infoAdicional: 'Extremamente úmido',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#0000d0',
			},
			{
				infoAdicional: 'Muito úmido',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#1b4cda',
			},
			{
				infoAdicional: 'Moderadamente úmido',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#00cb33',
			},
			{
				infoAdicional: 'Anormalmente úmido',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#65ff65',
			},
			{
				infoAdicional: 'Normal',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#ffffff',
			},
			{
				infoAdicional: 'Anormalmente seco',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#ffff66',
			},
			{
				infoAdicional: 'Moderadamente seco',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#ffcc00',
			},
			{
				infoAdicional: 'Severamente Seco',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#ff3300',
			},
			{
				infoAdicional: 'Extremamente Seco',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#d00000',
			},
			{
				infoAdicional: 'Excepcionalmente seco',
				valorMin: 0,
				valorMax: 0,
				unidade: '',
				cor: '#8b0000',
			},
		];
	}
	montarLegendaSpiPdf() {
		if (!this.valores || this.valores.length === 0) {
			this.valoresLegenda();
		}

		const primeiraLinha = this.valores.slice(0, 6);
		const segundaLinha = this.valores.slice(6);

		const montarLinha = (items: LegendaCor[]) => ({
			margin: [40, 4, 40, 4],
			columns: items.map(item => ({
				margin: [0, 2, 18, 2],
				columnGap: 6,
				columns: [
					{
						width: 14,
						canvas: [
							{
								type: 'ellipse',
								x: 6,
								y: 7,
								r1: 5,
								r2: 5,
								color: item.cor,
							},
						],
					},
					{
						text: item.infoAdicional,
						fontSize: 9,
						color: '#4f4f4f',
					},
				],
			})),
		});

		return {
			margin: [0, 20, 0, 0],
			stack: [montarLinha(primeiraLinha), montarLinha(segundaLinha)],
		};
	}

	getPeriodo() {
		const dataInicio = this.getFormItemValue('dataInicio');
		const dataFim = this.getFormItemValue('dataFim');
		return DateTimeUtils.formatarDataPeriodo(dataInicio, dataFim);
	}

	getDadosTabelaParaExportacao(dados: IapmDTO[], isPdf: boolean) {
		const tableData: any[][] = [];

		const colunas = [
			{ text: 'IAPM', fillColor: '#DCDCDC' },
			{ text: 'Período', fillColor: '#DCDCDC' },
		];

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		dados.forEach((item: IapmDTO) => {
			const rowData = [numberToBrNumber(item.iapm, 1), item.periodo];
			tableData.push(rowData);
		});

		return tableData;
	}

	private async getLogoBase64(caminho: string): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(caminho);
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error(`[SPI] Erro ao carregar logo ${caminho}:`, e);
			return null;
		}
	}

	async exportarPDF(graficoSPIUrl?: string) {
		if (!graficoSPIUrl && !this.graficoSPIBase64) {
			this.toastr.error(
				'Não é possível gerar o PDF porque nenhum gráfico SPI foi carregado.'
			);
			return;
		}

		const titulo = `Índice padronizado de precipitação (SPI) - ${this.getPeriodo()}`;
		const documentDefinition: any = await pdfseira.documentDefinitions(
			'landscape'
		);

		documentDefinition.pageOrientation = 'landscape';

		const logoAesaBase64 = await this.getLogoBase64(
			'assets/images/logos/logoAesa.svg'
		);
		const logoGovernoBase64 = await this.getLogoBase64(
			'assets/images/logos/governo_pb.png'
		);

		documentDefinition.images = documentDefinition.images || {};

		if (logoAesaBase64) {
			documentDefinition.images['aesaLogo'] = logoAesaBase64;
		}

		if (logoGovernoBase64) {
			documentDefinition.images['governoLogo'] = logoGovernoBase64;
		}

		documentDefinition.footer = (currentPage: number, pageCount: number) => {
			return {
				margin: [40, 0, 40, 20],
				columns: [
					logoAesaBase64
						? {
								image: 'aesaLogo',
								width: 60,
								alignment: 'left',
						  }
						: {},
					{
						text: `Página ${currentPage} de ${pageCount}`,
						alignment: 'center',
						fontSize: 8,
					},
					logoGovernoBase64
						? {
								image: 'governoLogo',
								width: 90,
								alignment: 'right',
						  }
						: {},
				],
			};
		};

		documentDefinition.content.push({
			text: titulo,
			fontSize: 12,
			alignment: 'center',
			margin: [0, 10],
		});

		try {
			documentDefinition.content.push({
				image: await this.getGraficoSPIParaExportacao(),
				alignment: 'center',
				width: 700,
				height: 350,
				margin: [0, 10, 0, 0],
			});
		} catch (e) {
			console.error('[SPI] Erro ao preparar gráfico para o PDF:', e);
			this.toastr.error('Ocorreu um erro ao montar o gráfico para o PDF.');
			return;
		}

		documentDefinition.content.push(this.montarLegendaSpiPdf());

		baixarEAbrirPDF(titulo, documentDefinition);
	}

	async exportarPNG() {
		if (!this.graficoSPIUrl && !this.graficoSPIBase64) {
			this.toastr.error(
				'Não é possível exportar PNG porque nenhum gráfico SPI foi carregado.'
			);
			return;
		}

		try {
			const dataUrl = await this.gerarPng();
			const link = document.createElement('a');
			link.href = dataUrl;

			const periodo = this.getPeriodo()?.replace(/[\s/:-]/g, '_') || 'spi';
			link.download = `spi_${periodo}.png`;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (e) {
			console.error('[SPI] Erro ao exportar PNG:', e);
			this.toastr.error('Ocorreu um erro ao gerar o PNG do SPI.');
		}
	}

	ngOnDestroy() {
		if (this.graficoSPIUrl) {
			URL.revokeObjectURL(this.graficoSPIUrl);
		}

		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.subscription.unsubscribe();
	}

	private getFormItemValue(name: string) {
		return this.inputs.form.get(name)?.value;
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private converterBlobParaDataUrl(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = error => reject(error);
			reader.readAsDataURL(blob);
		});
	}

	private async getGraficoSPIParaExportacao(): Promise<string> {
		if (this.graficoSPIBase64) {
			return this.graficoSPIBase64;
		}

		if (!this.graficoSPIUrl) {
			throw new Error('Grafico SPI ainda nao foi carregado.');
		}

		const graficoSPIUrl = this.graficoSPIUrl;

		if (graficoSPIUrl.startsWith('blob:')) {
			const response = await fetch(graficoSPIUrl);
			const blob = await response.blob();
			this.graficoSPIBase64 = await this.converterBlobParaDataUrl(blob);
			return this.graficoSPIBase64;
		}

		this.graficoSPIBase64 = await getBase64ImageFromURL(graficoSPIUrl);
		return this.graficoSPIBase64;
	}

	private async gerarPng(): Promise<string> {
		if (!this.graficoSPIUrl && !this.graficoSPIBase64) {
			throw new Error('Gráfico SPI ainda não foi carregado.');
		}

		if (!this.valores || this.valores.length === 0) {
			this.valoresLegenda();
		}

		const graficoExportacao = await this.getGraficoSPIParaExportacao();

		const [mapImage, logoAesa, logoGoverno] = await Promise.all([
			this.carregarImagem(graficoExportacao),
			this.carregarImagem('assets/images/logos/logoAesa.svg'),
			this.carregarImagem('assets/images/logos/governo_pb.png'),
		]);

		const mapWidth = mapImage.naturalWidth;
		const mapHeight = mapImage.naturalHeight;
		const legendHeight = Math.round(mapHeight * 0.3);

		const canvas = document.createElement('canvas');
		canvas.width = mapWidth;
		canvas.height = mapHeight + legendHeight;

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error('Canvas context não disponível');
		}

		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(mapImage, 0, 0, mapWidth, mapHeight);

		const yFaixa = mapHeight;
		ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
		ctx.fillRect(0, yFaixa, mapWidth, legendHeight);

		ctx.font = `${Math.round(mapHeight * 0.02)}px Arial`;
		ctx.textBaseline = 'middle';
		ctx.textAlign = 'left';

		const primeiraLinha = this.valores.slice(0, 6);
		const segundaLinha = this.valores.slice(6);

		const paddingLeft = mapWidth * 0.04;
		const paddingTopLegenda = yFaixa + legendHeight * 0.25;
		const gapLinha = legendHeight * 0.35;
		const raio = mapHeight * 0.015;

		const desenharLinha = (
			items: LegendaCor[],
			lineIndex: number,
			itensPorLinha: number
		) => {
			const larguraUtil = mapWidth - paddingLeft * 2;
			const espaco = larguraUtil / itensPorLinha;

			items.forEach((item, index) => {
				const cx = paddingLeft + espaco * index;
				const cy = paddingTopLegenda + lineIndex * gapLinha;

				ctx.beginPath();
				ctx.arc(cx, cy, raio, 0, Math.PI * 2);
				ctx.closePath();
				ctx.fillStyle = item.cor;
				ctx.fill();

				ctx.lineWidth = 1;
				ctx.strokeStyle = 'rgba(0,0,0,0.18)';
				ctx.stroke();

				ctx.fillStyle = '#4f4f4f';
				ctx.fillText(item.infoAdicional, cx + raio + 8, cy);
			});
		};

		desenharLinha(primeiraLinha, 0, primeiraLinha.length);
		desenharLinha(segundaLinha, 1, segundaLinha.length);

		const margem = mapWidth * 0.01;

		const larguraLogoAesa = mapWidth * 0.05;
		const alturaLogoAesa =
			(logoAesa.naturalHeight / logoAesa.naturalWidth) * larguraLogoAesa;
		const xLogoAesa = margem;
		const yLogoAesa = yFaixa + legendHeight - alturaLogoAesa - margem * 0.5;

		ctx.drawImage(
			logoAesa,
			xLogoAesa,
			yLogoAesa,
			larguraLogoAesa,
			alturaLogoAesa
		);

		const larguraLogoGoverno = mapWidth * 0.08;
		const alturaLogoGoverno =
			(logoGoverno.naturalHeight / logoGoverno.naturalWidth) *
			larguraLogoGoverno;
		const xLogoGoverno = mapWidth - larguraLogoGoverno - margem;
		const yLogoGoverno =
			yFaixa + legendHeight - alturaLogoGoverno - margem * 0.5;

		ctx.drawImage(
			logoGoverno,
			xLogoGoverno,
			yLogoGoverno,
			larguraLogoGoverno,
			alturaLogoGoverno
		);

		return canvas.toDataURL('image/png');
	}
}
